import { Pipe, PipeTransform, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, of, startWith, Subscription } from 'rxjs';
import { TextWithTranslatableParams } from 'shared/interfaces/multipart-translatable-text.interface';

/**
 * !NOTE: Needs to be used together with AsyncPipe!
 * Translate pipe for TextWithTranslatableParams arrays. Allows translation of the text key and all of its parameters.
 * The result will be the translated key, and all of its parameters that have translatable=true will also be translated before being used as a translation parameter for the key.
 */
@Pipe({
    name: 'multipartTranslate',
    pure: true,
})
export class MultipartTranslatePipe implements PipeTransform {
    constructor(
        private translate: TranslateService,
        private destroyRef: DestroyRef,
    ) {}

    transform(parts: TextWithTranslatableParams[], separator: string = ' '): Observable<string> {
        if (!parts || parts.length === 0) {
            return of('');
        }

        return this.translate.onLangChange.pipe(
            takeUntilDestroyed(this.destroyRef),
            startWith(null),
            map(() => this.translateParts(parts, separator)),
        );
    }

    private translateParts(parts: TextWithTranslatableParams[], separator: string): string {
        return parts
            .map((part) => {
                const translatedParams: Record<string, string> = {};

                if (part.params) {
                    part.params.forEach((param) => {
                        translatedParams[param.key] = param.translatable ? this.translate.instant(param.value) : param.value;
                    });
                }

                return this.translate.instant(part.key, translatedParams);
            })
            .join(separator);
    }
}
